import api from './api';
import type { PaginatedResponse } from './api';
import type { Member, Ad, MemberUsageResponse } from '../types';

/**
 * Get current member's profile
 */
export const getCurrentMember = async (): Promise<Member | null> => {
  const response = await api.get<Member>('/api/members/me');
  return response.success && response.data ? response.data : null;
};

/**
 * Update current member's profile
 * [UPDATED v0.7.1] 對齊後端 API 欄位名稱
 */
export const updateCurrentMember = async (data: {
  name?: string;
  phone?: string | null | undefined;
  storeName?: string | null | undefined;
  serviceAreas?: string[];
  serviceTypes?: string[];
}): Promise<Member | null> => {
  const response = await api.patch<Member>('/api/members/me', data);
  return response.success && response.data ? response.data : null;
};

/**
 * Get member's ads
 * [UPDATED v0.7.1] 對齊後端 API，使用 Ad 型別
 */
export const getMyAds = async (params?: {
  limit?: number;
  offset?: number;
}): Promise<PaginatedResponse<Ad>> => {
  const response = await api.get<{ data: Ad[]; pagination: { total: number; limit: number; offset: number } }>(
    '/api/members/me/ads',
    {
      ...params,
    }
  );

  if (response.success && response.data) {
    // Backend now returns member's ads filtered at server level
    const dataArray = Array.isArray(response.data) ? response.data : [];

    const pagination = (response as any).pagination || { total: 0, limit: 20, offset: 0 };

    return {
      data: dataArray,
      pagination: {
        total: pagination.total,
        limit: pagination.limit,
        offset: pagination.offset,
      },
    };
  }

  return {
    data: [],
    pagination: { total: 0, limit: 20, offset: 0 },
  };
};

/**
 * Create new ad
 * [UPDATED v0.7.1] 對齊後端 API，使用 Ad 型別
 * [FIXED v0.12.0] 新增 imageUrls 和 tags 欄位
 */
export const createAd = async (data: {
  title: string;
  description?: string;
  category: string;
  region: string;
  district?: string;
  storeName: string;
  phone?: string;
  address?: string;
  priceRange?: string;
  imageUrls?: string[];
  tags?: string[];
}): Promise<Ad | null> => {
  const response = await api.post<Ad>('/api/ads', data);
  return response.success && response.data ? response.data : null;
};

/**
 * Update ad
 * [UPDATED v0.7.1] 對齊後端 API，使用 Ad 型別
 * [FIXED v0.12.0] 新增 imageUrls 和 tags 欄位
 */
export const updateAd = async (
  id: string,
  data: {
    title?: string;
    description?: string;
    category?: string;
    region?: string;
    district?: string;
    storeName?: string;
    phone?: string;
    address?: string;
    priceRange?: string;
    imageUrls?: string[];
    tags?: string[];
  }
): Promise<Ad | null> => {
  const response = await api.put<Ad>(`/api/ads/${id}`, data);
  return response.success && response.data ? response.data : null;
};

/**
 * Delete ad
 */
export const deleteAd = async (id: string): Promise<boolean> => {
  const response = await api.delete<{ success: boolean }>(`/api/ads/${id}`);
  return response.success;
};

/**
 * Get member usage statistics
 */
export const getMemberUsage = async (): Promise<MemberUsageResponse['data'] | null> => {
  const response = await api.get<MemberUsageResponse['data']>('/api/members/me/usage');

  if (response.success && response.data) {
    return response.data;
  }

  return null;
};
